import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import './VendorStatementEmailInput.scss';
import { IconClear } from '../../../components/Icons';

export type VendorStatementEmailInputState = 'placeholder' | 'display' | 'error' | 'valid' | 'validating' | 'success';
export type VendorStatementEmailInputVariant = 'reply-to' | 'add-form' | 'default';

interface VendorStatementEmailInputProps {
  /** Current state of the input */
  state?: VendorStatementEmailInputState;
  /** Variant for different usage contexts */
  variant?: VendorStatementEmailInputVariant;
  /** Value for the email field */
  value?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Label for the field */
  label?: string;
  /** Whether to autofocus the input */
  autoFocus?: boolean;
  /** Callback when value changes */
  onValueChange?: (value: string) => void;
  /** Callback when state changes */
  onStateChange?: (state: VendorStatementEmailInputState) => void;
}

/**
 * VendorStatementEmailInput component for entering and validating email addresses
 *
 * Features five states:
 * - placeholder: Shows placeholder text with plus icon
 * - error: Shows invalid input with red exclamation mark
 * - valid: Shows valid email with green checkmark
 * - validating: Shows valid email with checkmark and loading spinner
 * - success: Shows confirmed email with green background and black checkmark
 */
const VendorStatementEmailInput: React.FC<VendorStatementEmailInputProps> = ({
  state = 'placeholder',
  variant = 'default',
  value = '',
  placeholder = 'Email',
  label = 'Email',
  autoFocus = false,
  onValueChange,
}) => {
  const [inputValue, setInputValue] = useState(value);

  // Update internal state when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onValueChange?.(newValue);
  };

  const handleClearInput = () => {
    setInputValue('');
    onValueChange?.('');
  };

  const renderIcon = () => {
    switch (state) {
      case 'placeholder':
        return (
          <div className="input-icon plus">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        );
      case 'error':
        return <div className="input-icon error">!</div>;
      case 'valid':
        return (
          <div className="input-icon valid">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13.5 4.5L6 12L2.5 8.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      case 'validating':
        return (
          <div className="input-icons">
            <div className="input-icon valid">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.5 4.5L6 12L2.5 8.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="input-icon spinner">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="37.7"
                  strokeDashoffset="37.7"
                />
              </svg>
            </div>
          </div>
        );
      case 'success':
        return (
          <div className="input-icon success">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13.5 4.5L6 12L2.5 8.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={classNames('vendor-statement-email-input', `state-${state}`, `variant-${variant}`)}>
      <div className="vendor-statement-field">
        {label && <div className="field-label">{label}</div>}
        <div className="field-content">
          <input
            type="email"
            value={inputValue}
            placeholder={state === 'placeholder' ? placeholder : ''}
            onChange={e => handleInputChange(e.target.value)}
            className="email-input"
            autoFocus={autoFocus}
          />
          {inputValue && (
            <button type="button" className="clear-button" onClick={handleClearInput} aria-label="Clear input">
              <IconClear />
            </button>
          )}
          {renderIcon()}
        </div>
      </div>
    </div>
  );
};

export default VendorStatementEmailInput;
